import React, { createContext, useState, useContext, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const QuestionBankContext = createContext();

export const QuestionBankProvider = ({ children }) => {
  const [questionBank, setQuestionBank] = useState(() => {
    // Load from localStorage if available
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('questionBank');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  // Save to localStorage whenever questionBank changes
  const saveToLocalStorage = (newBank) => {
  };

  // Add questions to the bank
  const addToQuestionBank = useCallback((questions) => {
    if (!Array.isArray(questions)) questions = [questions];
    
    const newQuestions = questions.map(question => ({
      ...question,
      id: question.id || `bank-${uuidv4()}`,
      bankId: `bank-${uuidv4()}`,
      addedAt: new Date().toISOString(),
      tags: Array.isArray(question.tags) ? question.tags : [],
      usedCount: 0
    }));

    setQuestionBank(prevBank => {
      const updatedBank = [
        ...newQuestions,
        ...prevBank.filter(
          q => !newQuestions.some(nq => 
            nq.id === q.id || 
            nq.bankId === q.bankId ||
            nq.question.toLowerCase() === q.question.toLowerCase()
          )
        )
      ];
      saveToLocalStorage(updatedBank);
      return updatedBank;
    });
  }, []);

  // Remove questions from the bank
  const removeFromQuestionBank = useCallback((questionIds) => {
    if (!Array.isArray(questionIds)) questionIds = [questionIds];
    
    setQuestionBank(prevBank => {
      const updatedBank = prevBank.filter(q => !questionIds.includes(q.bankId || q.id));
      saveToLocalStorage(updatedBank);
      return updatedBank;
    });
  }, []);

  // Update a question in the bank
  const updateQuestionInBank = useCallback((questionId, updates) => {
    setQuestionBank(prevBank => {
      const updatedBank = prevBank.map(q => 
        q.bankId === questionId || q.id === questionId 
          ? { ...q, ...updates, updatedAt: new Date().toISOString() } 
          : q
      );
      saveToLocalStorage(updatedBank);
      return updatedBank;
    });
  }, []);

  // Increment the used count for questions
  const incrementUsedCount = useCallback((questionIds) => {
    setQuestionBank(prevBank => {
      const updatedBank = prevBank.map(q => 
        questionIds.includes(q.bankId || q.id)
          ? { ...q, usedCount: (q.usedCount || 0) + 1 }
          : q
      );
      saveToLocalStorage(updatedBank);
      return updatedBank;
    });
  }, []);

  // Search questions in the bank
  const searchQuestions = useCallback((query, filters = {}) => {
    const { tags = [], minUsed = 0, maxUsed = Infinity, sortBy = 'addedAt', sortOrder = 'desc' } = filters;
    
    return questionBank.filter(question => {
      // Text search
      const matchesText = !query || 
        question.question.toLowerCase().includes(query.toLowerCase()) ||
        (question.explanation && question.explanation.toLowerCase().includes(query.toLowerCase()));
      
      // Tag filter
      const matchesTags = tags.length === 0 || 
        tags.every(tag => question.tags?.includes(tag));
      
      // Usage count filter
      const matchesUsage = (question.usedCount || 0) >= minUsed && 
                         (question.usedCount || 0) <= maxUsed;
      
      return matchesText && matchesTags && matchesUsage;
    }).sort((a, b) => {
      // Sorting
      let comparison = 0;
      if (sortBy === 'addedAt') {
        comparison = new Date(a.addedAt) - new Date(b.addedAt);
      } else if (sortBy === 'usedCount') {
        comparison = (a.usedCount || 0) - (b.usedCount || 0);
      } else if (sortBy === 'question') {
        comparison = a.question.localeCompare(b.question);
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [questionBank]);

  return (
    <QuestionBankContext.Provider
      value={{
        questionBank,
        addToQuestionBank,
        removeFromQuestionBank,
        updateQuestionInBank,
        incrementUsedCount,
        searchQuestions,
      }}
    >
      {children}
    </QuestionBankContext.Provider>
  );
};

export const useQuestionBank = () => {
  const context = useContext(QuestionBankContext);
  if (!context) {
    throw new Error('useQuestionBank must be used within a QuestionBankProvider');
  }
  return context;
};

export default QuestionBankContext;
